import { InvitationLogEntryType } from '@api/models/enums/InvitationLogEntryType';
import { CancelToken } from 'axios';
import { FC } from 'react';
import { useAppSelector } from '../../../../store';
import {
  getInvitationHistoryList,
  invitationHistoryListActions,
  selectInvitationHistoryList
} from '../../../../store/entities/invitationHistoryList';
import { getLangNameObj } from '../../../../utils/format';
import { QcsTableCell } from '../../../common/basic/QcsTableCell';
import { CellDate } from '../../../common/grid/CellDate';
import { Grid } from '../../../common/grid/Grid';
import { useInvitation } from './invitationFunctions';
import { useNavigate } from 'react-router';
import { InvitationLogEntryResponse } from '@api/models/invitation/InvitationLogEntryResponse';
import { InvitationLogEntryParamKey } from '@api/models/enums/InvitationLogEntryParamKey';
import { InvTabLogsFilter } from './InvTabLogsFilter';

export const InvTabLogs: FC = () => {
  const { t, i18n, invitation, dispatch } = useInvitation();
  const invitationHistoryList = useAppSelector(selectInvitationHistoryList);
  const navigate = useNavigate();
  const backTo = encodeURIComponent(`/invitation/${invitation.id}/log`);

  const getDetailLink = (item: InvitationLogEntryResponse): string | undefined => {
    switch (item.type) {
      case InvitationLogEntryType.EntryRequestCreated:
      case InvitationLogEntryType.EntryRequestApproved:
      case InvitationLogEntryType.EntryRequestRejected:
      case InvitationLogEntryType.EntryRequestChanged:
        return `/invitation/${invitation.id}/entryRequest?entryRequestId=${item.params![InvitationLogEntryParamKey.EntryRequestId]}&backTo=${backTo}`;
      case InvitationLogEntryType.ActivityAdded:
      case InvitationLogEntryType.ActivityRemoved:
      case InvitationLogEntryType.ActivityDocumentRemoved:
        return `/activity/${item.params![InvitationLogEntryParamKey.ActivityId]}?backTo=${backTo}`;
      case InvitationLogEntryType.WorkplaceAdded:
      case InvitationLogEntryType.WorkplaceRemoved:
        return `/workplace/${item.params![InvitationLogEntryParamKey.WorkplaceId]}?backTo=${backTo}`;
      default:
        return undefined;
    }
  };

  const linkStyle = {
    background: 'none',
    border: 'none',
    color: '#0066cc',
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: 0,
    font: 'inherit',
  } as const;

  const handleGetData = (cancelToken: CancelToken) => {
    dispatch(getInvitationHistoryList(invitation.id!, cancelToken));
  };

  const DATE_PARAMS = new Set<string>([
    InvitationLogEntryParamKey.RiskVersionValidFrom,
  ]);

  const formatParamValue = (key: string, value: string): string => {
    if (DATE_PARAMS.has(key) && value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString(i18n.language, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      }
    }
    return value;
  };

  const handleRenderData = (item: InvitationLogEntryResponse) => {
    let text = t('invitation.log.types.' + item.type);
    Object.keys(item.params ?? {}).forEach((x) => {
      const value = item.params![x as InvitationLogEntryParamKey] ?? '';
      text = text.replaceAll('{' + x + '}', formatParamValue(x, value));
    });
    text = text.replaceAll(/\{[A-Z_]+\}/g, '–');

    const detailLink = getDetailLink(item);
    const linkMatch = text.match(/^(.*)<link>(.*)<\/link>(.*)$/);

    const renderText = () => {
      if (!linkMatch || !detailLink) {
        return <>{text}</>;
      }

      const [, before, linkText, after] = linkMatch;
      return (
        <>
          {before}
          <button
            onClick={() => navigate(detailLink)}
            style={linkStyle}
            onMouseEnter={(e) => ((e.target as HTMLElement).style.textDecoration = 'none')}
            onMouseLeave={(e) => ((e.target as HTMLElement).style.textDecoration = 'underline')}
          >
            {linkText}
          </button>
          {after}
        </>
      );
    };

    return (
      <>
        <CellDate value={item.timestamp} showWithTime={true} />
        <QcsTableCell>{getLangNameObj(i18n, item.actionBy)}</QcsTableCell>
        <QcsTableCell>{renderText()}</QcsTableCell>
      </>
    );
  };

  return (
    <Grid<InvitationLogEntryResponse>
      headers={[
        { captionStr: 'invitation.log.timestamp', orderName: 'timestamp' },
        { captionStr: 'invitation.log.actionBy' },
        { captionStr: 'invitation.log.type' },
      ]}
      data={invitationHistoryList}
      gridActions={invitationHistoryListActions}
      renderData={handleRenderData}
      getData={handleGetData}
      search={false}
      filter={<InvTabLogsFilter query={invitationHistoryList.query} />}
    />
  );
};
